import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ImagePlus, LogOut, Package, Pencil, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { api, authHeaders, uploadFile } from "@/lib/api";
import { refreshSettings } from "@/lib/settings";
import { LogoMark } from "@/components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Graphic Tees", "Heavy Oversized", "Acid Wash", "Minimal Archival", "Custom Bags"];
const SIZES = ["S", "M", "L", "XL", "XXL"];

const inputCls =
  "w-full bg-[#F7F7F2] border border-[#E4E4DE] px-4 py-3 text-sm text-zinc-900 placeholder-zinc-600 focus:border-[#C8FF1E] focus:outline-none transition-colors";

const emptyForm = () => ({
  name: "",
  category: CATEGORIES[0],
  price: "",
  gsm: "280 GSM",
  color: "",
  tag: "",
  description: "",
  main_image: "",
  model_image: "",
  flatlay_image: "",
  featured: false,
  sizes: SIZES.map((s) => ({ size: s, stock: 10 })),
});

const ImageField = ({ testid, placeholder, value, onChange, required = false }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadFile(f);
      onChange(url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed — try a smaller image");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          data-testid={testid}
          className={inputCls}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required && !value}
        />
        <button
          type="button"
          data-testid={`${testid}-upload-btn`}
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="flex shrink-0 items-center gap-2 border border-[#E4E4DE] px-4 font-mono text-[10px] uppercase tracking-widest text-zinc-600 hover:border-[#C8FF1E] hover:text-[#4D7C0F] disabled:opacity-40 transition-colors"
        >
          <ImagePlus size={14} /> {busy ? "Uploading…" : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
      </div>
      {value && (
        <img src={value} alt="" className="mt-2 h-16 w-12 border border-[#E4E4DE] object-cover" />
      )}
    </div>
  );
};

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ka_admin_token", data.access_token);
      onSuccess(data.access_token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login" className="flex min-h-screen items-center justify-center px-4 pt-16">
      <motion.form
        onSubmit={submit}
        className="w-full max-w-sm border border-[#E4E4DE] bg-[#FFFFFF] p-8"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <LogoMark size={36} />
        <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tighter">Studio admin</h1>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Restricted — store owners only
        </p>
        <div className="mt-6 space-y-4">
          <input
            data-testid="admin-login-email"
            className={inputCls}
            type="email"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            data-testid="admin-login-password"
            className={inputCls}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-3 font-mono text-xs text-red-500">{error}</p>
        )}
        <button
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-6 w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
        >
          {loading ? "Unlocking…" : "Enter dashboard"}
        </button>
      </motion.form>
    </main>
  );
}

const SettingsTab = () => {
  const [logoUrl, setLogoUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setLogoUrl(data.logo_url || "");
      setSavedUrl(data.logo_url || "");
    });
  }, []);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadFile(f);
      setLogoUrl(url);
      toast.success("Logo uploaded — save to apply it");
    } catch {
      toast.error("Upload failed — try a smaller image");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { logo_url: logoUrl }, authHeaders());
      await refreshSettings();
      setSavedUrl(logoUrl);
      toast.success("Logo updated across the site");
    } catch {
      toast.error("Could not save logo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="mt-6 max-w-xl border border-[#E4E4DE] p-8">
      <h2 className="font-display text-2xl font-extrabold uppercase tracking-tighter">Site logo</h2>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed">
        Shown in the header, footer and browser tab. Square PNG or SVG with transparency works best.
      </p>
      <div className="mt-6 flex items-center gap-6">
        <div className="flex h-24 w-24 items-center justify-center border border-[#E4E4DE] bg-[#F7F7F2]">
          {logoUrl ? (
            <img data-testid="logo-preview" src={logoUrl} alt="Logo preview" className="max-h-20 max-w-20 object-contain" />
          ) : (
            <LogoMark size={40} />
          )}
        </div>
        <div className="space-y-3">
          <button
            data-testid="logo-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 border border-[#E4E4DE] px-5 py-3 font-mono text-xs uppercase tracking-widest text-zinc-700 hover:border-[#C8FF1E] hover:text-[#4D7C0F] disabled:opacity-40 transition-colors"
          >
            <ImagePlus size={14} /> {busy ? "Uploading…" : "Upload logo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="logo-file-input" />
          {logoUrl && (
            <button
              data-testid="logo-remove-btn"
              onClick={() => setLogoUrl("")}
              className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-500 transition-colors"
            >
              Remove custom logo
            </button>
          )}
        </div>
      </div>
      <button
        data-testid="logo-save-btn"
        onClick={save}
        disabled={busy || logoUrl === savedUrl}
        className="mt-8 bg-[#C8FF1E] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
      >
        Save logo
      </button>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("ka_admin_token") || "");
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [p, o] = await Promise.all([
      api.get("/products"),
      api.get("/admin/orders", authHeaders()),
    ]);
    setProducts(p.data);
    setOrders(o.data);
  }, []);

  useEffect(() => {
    if (!token) return;
    load().catch(() => {
      localStorage.removeItem("ka_admin_token");
      setToken("");
    });
  }, [token, load]);

  if (!token) return <LoginForm onSuccess={setToken} />;

  const logout = () => {
    localStorage.removeItem("ka_admin_token");
    setToken("");
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      category: p.category,
      price: p.price,
      gsm: p.gsm,
      color: p.color,
      tag: p.tag,
      description: p.description,
      main_image: p.main_image,
      model_image: p.model_image,
      flatlay_image: p.flatlay_image,
      featured: p.featured,
      sizes: SIZES.map((s) => {
        const found = (p.sizes || []).find((x) => x.size === s);
        return { size: s, stock: found ? found.stock : 0 };
      }),
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload, authHeaders());
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload, authHeaders());
        toast.success("Product added to the archive");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, authHeaders());
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Could not delete product");
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCategory = (e) => {
    const category = e.target.value;
    setForm((f) => ({
      ...f,
      category,
      sizes:
        category === "Custom Bags"
          ? [{ size: "One Size", stock: f.sizes.length === 5 ? 10 : f.sizes[0]?.stock ?? 10 }]
          : f.sizes.length === 1
            ? SIZES.map((s) => ({ size: s, stock: 10 }))
            : f.sizes,
    }));
  };
  const setImage = (k) => (url) => setForm((f) => ({ ...f, [k]: url }));
  const setStock = (size, value) =>
    setForm({
      ...form,
      sizes: form.sizes.map((s) => (s.size === size ? { ...s, stock: parseInt(value) || 0 } : s)),
    });

  const totalStock = products.reduce(
    (n, p) => n + (p.sizes || []).reduce((m, s) => m + s.stock, 0),
    0
  );
  const revenue = orders.reduce((n, o) => n + (o.total || 0), 0);

  return (
    <main data-testid="admin-dashboard" className="pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">Studio admin</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter">
              Manage the archive
            </h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-[#E4E4DE] px-5 py-3 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:border-red-500 hover:text-red-500 transition-colors"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>

        <div className="mt-10 grid gap-px border border-[#E4E4DE] bg-[#E4E4DE] sm:grid-cols-3">
          {[
            [products.length, "Products live"],
            [orders.length, "Orders placed"],
            [`$${revenue.toFixed(0)}`, "Total revenue"],
          ].map(([value, label], i) => (
            <div key={label} data-testid={`admin-metric-${i}`} className="bg-[#F7F7F2] p-6">
              <p className="font-display text-3xl font-extrabold text-[#4D7C0F]">{value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          {totalStock} units in stock across all sizes
        </p>

        <div className="mt-8 flex gap-2">
          {["products", "orders", "settings"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 border font-mono text-xs uppercase tracking-widest transition-colors ${
                tab === t
                  ? "border-[#C8FF1E] bg-[#C8FF1E] text-black font-bold"
                  : "border-[#E4E4DE] text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="mt-6">
            <button
              data-testid="admin-add-product-btn"
              onClick={openAdd}
              className="inline-flex items-center gap-2 bg-[#C8FF1E] px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-black hover:bg-[#B0E600] transition-colors"
            >
              <Plus size={14} /> Add new product
            </button>
            <div className="mt-6 space-y-3 sm:hidden">
              {products.map((p) => {
                const stock = (p.sizes || []).reduce((n, s) => n + s.stock, 0);
                return (
                  <div key={p.id} data-testid={`admin-product-card-${p.id}`} className="border border-[#E4E4DE] bg-white p-4">
                    <div className="flex gap-3">
                      <img src={p.main_image} alt="" className="h-16 w-12 border border-[#E4E4DE] object-cover" />
                      <div className="flex-1">
                        <p className="font-display text-sm font-bold uppercase tracking-tight">{p.name}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                          {p.category} · <span className="text-[#4D7C0F]">${p.price}</span>
                        </p>
                        <p className={`mt-1 font-mono text-[10px] uppercase tracking-widest ${stock === 0 ? "text-red-500" : stock <= 15 ? "text-orange-500" : "text-zinc-500"}`}>
                          {stock === 0 ? "Sold out" : `${stock} units in stock`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        data-testid={`admin-edit-mobile-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="flex-1 inline-flex items-center justify-center gap-2 border border-[#E4E4DE] py-3 font-mono text-xs uppercase tracking-widest text-zinc-600 hover:border-[#C8FF1E] hover:text-[#4D7C0F] transition-colors"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        data-testid={`admin-delete-mobile-${p.id}`}
                        onClick={() => remove(p)}
                        className="flex-1 inline-flex items-center justify-center gap-2 border border-[#E4E4DE] py-3 font-mono text-xs uppercase tracking-widest text-zinc-600 hover:border-red-500 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 overflow-x-auto border border-[#E4E4DE] hidden sm:block">
              <table data-testid="admin-products-table" className="w-full min-w-[760px] text-left">
                <thead>
                  <tr className="border-b border-[#E4E4DE] font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Stock</th>
                    <th className="px-4 py-3">Tag</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4E4DE]">
                  {products.map((p) => {
                    const stock = (p.sizes || []).reduce((n, s) => n + s.stock, 0);
                    return (
                      <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="hover:bg-[#EFEFEA] transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img src={p.main_image} alt="" className="h-12 w-10 border border-[#E4E4DE] object-cover" />
                            <span className="font-display text-sm font-bold uppercase tracking-tight">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">{p.category}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#4D7C0F]">${p.price}</td>
                        <td className="px-4 py-3">
                          <span
                            data-testid={`admin-stock-${p.id}`}
                            className={`font-mono text-xs ${stock === 0 ? "text-red-500" : stock <= 15 ? "text-orange-500" : "text-zinc-600"}`}
                          >
                            {stock === 0 ? "Sold out" : `${stock} units`}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.tag || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              data-testid={`admin-edit-${p.id}`}
                              onClick={() => openEdit(p)}
                              className="h-9 w-9 flex items-center justify-center border border-[#E4E4DE] text-zinc-500 hover:border-[#C8FF1E] hover:text-[#4D7C0F] transition-colors"
                              aria-label="Edit product"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              data-testid={`admin-delete-product-btn-${p.id}`}
                              onClick={() => remove(p)}
                              className="h-9 w-9 flex items-center justify-center border border-[#E4E4DE] text-zinc-500 hover:border-red-500 hover:text-red-500 transition-colors"
                              aria-label="Delete product"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-6">
            {orders.length === 0 ? (
              <p data-testid="admin-orders-empty" className="border border-[#E4E4DE] px-6 py-16 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">
                No orders yet
              </p>
            ) : (
              <>
              <div className="space-y-3 sm:hidden">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`admin-order-card-${o.order_no}`} className="border border-[#E4E4DE] bg-white p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-xs font-bold text-[#4D7C0F]">{o.order_no}</p>
                      <span className="bg-[#C8FF1E]/20 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#4D7C0F]">
                        {o.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-zinc-700">{o.customer?.name}</p>
                    <p className="font-mono text-[10px] text-zinc-500">{o.customer?.email}</p>
                    <p className="mt-2 font-mono text-xs text-zinc-500">
                      {(o.items || []).map((i) => `${i.name} (${i.size}) ×${i.qty}`).join(", ")}
                    </p>
                    <div className="mt-2 flex justify-between font-mono text-xs">
                      <span className="text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</span>
                      <span className="font-bold text-zinc-900">${o.total?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto border border-[#E4E4DE] hidden sm:block">
              <table data-testid="admin-orders-table" className="w-full min-w-[760px] text-left">
                <thead>
                  <tr className="border-b border-[#E4E4DE] font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4E4DE]">
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`admin-order-row-${o.order_no}`} className="hover:bg-[#EFEFEA] transition-colors">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-[#4D7C0F]">{o.order_no}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-zinc-700">{o.customer?.name}</p>
                        <p className="font-mono text-[10px] text-zinc-500">{o.customer?.email}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                        {(o.items || []).map((i) => `${i.name} (${i.size}) ×${i.qty}`).join(", ")}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-900">${o.total?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className="bg-[#C8FF1E]/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#4D7C0F]">
                          {o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-[10px] text-zinc-500">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </>
            )}
          </div>
        )}

        {tab === "settings" && <SettingsTab />}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="admin-product-dialog"
          className="max-h-[90vh] overflow-y-auto border border-[#E4E4DE] bg-[#FFFFFF] text-zinc-900 sm:max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold uppercase tracking-tighter">
              {editingId ? "Edit product" : "Add new product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="admin-form-name" className={inputCls} placeholder="Product name" value={form.name} onChange={setField("name")} required />
              <select data-testid="admin-form-category" className={inputCls} value={form.category} onChange={setCategory}>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <input data-testid="admin-form-price" className={inputCls} type="number" step="0.01" min="0" placeholder="Price (USD)" value={form.price} onChange={setField("price")} required />
              <input data-testid="admin-form-gsm" className={inputCls} placeholder="Fabric weight (e.g. 280 GSM)" value={form.gsm} onChange={setField("gsm")} />
              <input data-testid="admin-form-color" className={inputCls} placeholder="Colorway" value={form.color} onChange={setField("color")} />
              <input data-testid="admin-form-tag" className={inputCls} placeholder="Tag (e.g. NEW DROP, LIMITED 100)" value={form.tag} onChange={setField("tag")} />
            </div>
            <textarea
              data-testid="admin-form-description"
              className={`${inputCls} min-h-24 resize-y`}
              placeholder="Description"
              value={form.description}
              onChange={setField("description")}
            />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Main photo (shown in shop grid)</p>
              <div className="mt-2">
                <ImageField testid="admin-form-main-image" placeholder="Image URL or upload →" value={form.main_image} onChange={setImage("main_image")} required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">On-model photo</p>
                <div className="mt-2">
                  <ImageField testid="admin-form-model-image" placeholder="URL or upload" value={form.model_image} onChange={setImage("model_image")} />
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Flat-lay photo</p>
                <div className="mt-2">
                  <ImageField testid="admin-form-flatlay-image" placeholder="URL or upload" value={form.flatlay_image} onChange={setImage("flatlay_image")} />
                </div>
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {form.sizes.length === 1 ? "Stock (one size)" : "Stock per size"}
              </p>
              <div className="mt-2 grid grid-cols-5 gap-2">
                {form.sizes.map((s) => (
                  <label key={s.size} className="block">
                    <span className="mb-1 block text-center font-mono text-[10px] uppercase text-zinc-500">{s.size}</span>
                    <input
                      data-testid={`admin-form-stock-${s.size.toLowerCase()}`}
                      className={`${inputCls} px-2 text-center`}
                      type="number"
                      min="0"
                      value={s.stock}
                      onChange={(e) => setStock(s.size, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-zinc-500">
              <input
                data-testid="admin-form-featured"
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="h-4 w-4 accent-[#C8FF1E]"
              />
              Feature on homepage
            </label>
            <button
              data-testid="admin-form-save-btn"
              disabled={saving}
              className="w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Add to the archive"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
